import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage.service';
import { storageTokenKeys } from '../../constants/constants';
import { decodeTokenPayload } from './decode-token';
import { AccountInfo } from '../../models/account-info';
import { UserRole } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private readonly localStorageService = inject(LocalStorageService);

  getAccountInfo(): AccountInfo | null {
    const token = this.localStorageService.get(storageTokenKeys.accessToken);
    if (!token) {
      return null;
    }

    try {
      const payload = decodeTokenPayload(token);
      return {
        userId: (payload['sub'] || payload['userId'] || null) as string | null,
        position: (payload['userJobTitle'] || null) as string | null,
        phone: (payload['userPhoneNumber'] || null) as string | null,
        name: this.getName(payload),
        email: (payload['email'] || null) as string | null,
        company: this.getCompany(payload),
        roles: this.extractRoles(payload),
      };
    } catch {
      return null;
    }
  }

  private getCompany(payload: Record<string, unknown>) {
    if(!payload['tenantName']) {
      return null;
    }
    return {
      name: payload['tenantName'] as string,
      id: payload['tenantId'] as string,
    };
  }

  private getName(payload: { [p: string]: unknown }) {
    if(!payload['userFN'] || !payload['userLN']) {
      return null;
    }
    return (payload['userFN'] as string) + ' ' + (payload['userLN'] as string);
  }

  getUserId(): string | null {
    return this.getAccountInfo()?.userId ?? null;
  }

  getUserRoles(): UserRole[] {
    return this.getAccountInfo()?.roles ?? [];
  }

  private extractRoles(payload: Record<string, unknown>): UserRole[] {
    const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || [];
    if (Array.isArray(roles)) {
      return roles as UserRole[];
    }
    return [roles as UserRole];
  }
}
